/**
 * Client-side scoring helpers. Mirrors the Python scoring system
 * (`scorer.py` + `combined_scorer.py`). The build:data script is the single
 * source of truth — these helpers are only used for derived displays
 * (tier, status, color, delta sign).
 */

import type { Category, ScoreTier, SkillStatus } from "./types";

const TIER_THRESHOLDS: Array<[ScoreTier, number]> = [
  ["excellent", 84],
  ["good", 64],
  ["needs-work", 40],
  ["poor", 0],
];

/** Tier from a combined/structural percentage (mirrors combined_scorer.py). */
export function tierForPercentage(pct: number): ScoreTier {
  for (const [t, threshold] of TIER_THRESHOLDS) {
    if (pct >= threshold) return t;
  }
  return "poor";
}

/** Bucket == tier; kept as a distinct name for filter/stat grouping. */
export const bucketFor = tierForPercentage;

export function categoryFor(skillName: string): Category {
  if (skillName.startsWith("agent-")) return "agent";
  if (skillName.startsWith("skill-")) return "skill";
  if (skillName.startsWith("utrecht-")) return "utrecht";
  if (skillName.startsWith("workflow-")) return "workflow";
  return "other";
}

type ColorClass = "good" | "warn" | "bad";

/** Color class for a percentage. excellent/good → green, needs-work → orange, poor → red. */
export function scoreClass(pct: number): ColorClass {
  if (pct >= 64) return "good";
  if (pct >= 40) return "warn";
  return "bad";
}

export function scoreClassText(pct: number): "text-good" | "text-warn" | "text-bad" {
  return `text-${scoreClass(pct)}` as const;
}

/** Color class for a tier. */
export function tierClass(tier: ScoreTier): ColorClass {
  if (tier === "excellent" || tier === "good") return "good";
  if (tier === "needs-work") return "warn";
  return "bad";
}

export const TIER_LABEL: Record<ScoreTier, string> = {
  excellent: "Excellent",
  good: "Good",
  "needs-work": "Needs work",
  poor: "Poor",
};

/** Color class for an operational status. */
export function statusClass(status: SkillStatus): ColorClass {
  if (status === "healthy") return "good";
  if (status === "warning") return "warn";
  return "bad";
}

export const STATUS_LABEL: Record<SkillStatus, string> = {
  healthy: "Healthy",
  warning: "Warning",
  critical: "Critical",
};

export function deltaSign(delta: number | null): string {
  if (delta === null) return "—";
  if (delta > 0) return `+${delta}`;
  return `${delta}`;
}

export function deltaClass(delta: number | null): "good" | "bad" | "flat" {
  if (delta === null || delta === 0) return "flat";
  return delta > 0 ? "good" : "bad";
}